import React, { useRef } from "react";

import styled from "styled-components/macro";

import TierPriceViewer from "Payments/TierPricing/TierPriceViewer/TierPriceViewer";
import { COST_TYPES } from "Signups/SharedComponents/costTypes";
import { useSignups } from "Signups/state.js";
import { useBackgroundColor, useHasDescriptionOrMedia } from "Signups/stateHooks";
import { AnimatePresence, motion } from "framer-motion";

import { EditorTypography } from "UI/FnrTextEditor/EditorTypography";
import { wrapLongWords } from "UI/styles";

import useResizeObserver from "Utilities/Hooks/useResizeObserver";
import { useScroll } from "Utilities/Hooks/useScroll";
import removeHtmlTags from "Utilities/removeHtmlTags";

import { Container } from "../Styles";

import ImageVideo from "./ImageVideo";
import { animate, initial } from "./animation";

export default function SignupHeader({ headerRef }) {
  const titleWrapperRef = useRef();
  const [state, dispatch] = useSignups();

  const {
    title,
    description,
    signupListVerbs: { entity } = {},
    fundIt: {
      cost: { costType, amounts: tierData } = {},
      isSuggestedDonation,
    } = {},
  } = state.currentSignupList;
  const shouldDisplayCost = costType === COST_TYPES.PER_SIGNUP;
  const hasDescription = removeHtmlTags(description)?.trim()?.length > 0;
  const { hasTwoColumns } = useHasDescriptionOrMedia();
  const { colorVariant, backgroundColorScheme } = useBackgroundColor();

  const { positionY } = useScroll();

  // used in partnership with PayNowArea to allow the payment UI to scroll, but stick to the top of the page
  // const headerRef = useRef();
  useResizeObserver(headerRef, () => {
    dispatch({
      type: "setPublicHeaderHeight",
      payload: headerRef?.current?.getBoundingClientRect().height,
    });
  });

  return (
    <Wrapper ref={headerRef} $lightMode={colorVariant === "light"}>
      {/* DummyDiv fills the space in the dom when FixedTitleWrapper gets set to fixed and pulled out of the normal flow */}
      {positionY > 20 && (
        <DummyDiv
          $fixedTitleHeight={headerRef?.current?.getBoundingClientRect().height}
        />
      )}
      <FixedTitleWrapper
        ref={titleWrapperRef}
        $isScrolled={positionY > 20}
        $backgroundColor={backgroundColorScheme[0]}
      >
        <SContainer $wide={hasTwoColumns}>
          <AnimatePresence>
            <Title
              $isScrolled={positionY > 20}
              initial={initial}
              animate={animate}
              data-cy="public-signup-title"
            >
              {title}
            </Title>
          </AnimatePresence>

          {shouldDisplayCost ? (
            <PriceContainer
              hasDescription={hasDescription}
              $lightMode={colorVariant === "light"}
              $hasTwoColumns={hasTwoColumns}
            >
              <TierPriceViewer
                tierData={tierData}
                suggestedDonation={isSuggestedDonation}
                entity={tierData.length > 1 ? entity : "household"}
                dark={colorVariant === "dark"}
              />
            </PriceContainer>
          ) : null}

          <AnimatePresence>
            {!hasTwoColumns && (
              <>
                <ImageVideo />
                <Description
                  data-cy="Signup_PublicDescription"
                  $hasTwoColumns={hasTwoColumns}
                  initial={{ opacity: 0, y: "50" }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <DescriptionContent
                    dangerouslySetInnerHTML={{
                      __html: description,
                    }}
                  />
                </Description>
              </>
            )}
          </AnimatePresence>
        </SContainer>
      </FixedTitleWrapper>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  width: 100%;
  color: ${({ $lightMode }) => ($lightMode ? "#383838" : "white")};
  min-height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

const DummyDiv = styled.div`
  height: ${({ $fixedTitleHeight }) => $fixedTitleHeight}px;
  transition: height 0.25s;
  will-change: height;
`;

const FixedTitleWrapper = styled.div`
  width: 100%;
  will-change: background-color;
  transition: background-color 0.25s;

  @media screen and (min-width: 1000px) {
    position: ${({ $isScrolled }) => ($isScrolled ? "fixed" : "relative")};
    top: 0;
    padding: ${({ $isScrolled }) => ($isScrolled ? 0 : "20px 0")};
    background-color: ${({ $isScrolled, $backgroundColor }) =>
      $isScrolled ? `${$backgroundColor}ff` : `${$backgroundColor}00`};
    box-shadow: ${({ $isScrolled }) =>
      $isScrolled
        ? "0 2px 5px hsl(0 0% 0% / 0.25)"
        : "0 2px 5px hsl(0 0% 0% / 0)"};
  }
`;

const SContainer = styled(Container)`
  max-width: ${({ $wide }) => ($wide ? "1024px" : "600px")};

  @media screen and (max-width: 999px) {
    max-width: 600px;
  }
`;

const Title = styled(motion.h1)`
  margin: 0;
  margin-block: ${({ $isScrolled }) => ($isScrolled ? "7px 10px" : "10px")};
  font-weight: 400;
  font-size: 34px;
  padding-inline: 28px;
  ${wrapLongWords}
`;

const PriceContainer = styled.div`
  width: max-content;
  margin-block-end: ${(p) =>
    p.hasDescription || !p.$hasTwoColumns ? "18px" : 0};
  color: ${({ $lightMode }) =>
    $lightMode ? "#383838 !important;" : "white !important"};
  padding-inline: 28px;

  * {
    font-size: 20px;
    color: ${({ $lightMode }) =>
      $lightMode ? "#383838 !important;" : "white !important"};

    span,
    span + div {
      margin-block: -3px;
    }
  }

  & button:hover {
    background-color: ${({ $lightMode }) =>
      $lightMode
        ? "hsl(0 0% 0% / 0.1) !important"
        : "hsl(0 0% 100% / 0.25) !important"};
  }

  @media screen and (max-width: 999px) {
    padding-inline: 28px;
  }
`;

const Description = styled(motion.div)`
  opacity: 0.75;
  font-size: 16px;
  padding-inline: ${({ $hasTwoColumns }) => ($hasTwoColumns ? 0 : "28px")};
  ${wrapLongWords}

  @media screen and (max-width: 999px) {
    padding-inline: 28px;
  }
`;

const DescriptionContent = styled(EditorTypography)`
  font-weight: 400;
`;